import type { ManualEntryContext, ManualEntrySource } from "../types/manualEntry";

export const TICKET_ENTRY_ROLES = ["学生", "老师", "导游", "司机"] as const;
export type TicketEntryField = "name" | "mode" | "role" | "people" | "count" | "price";
export type TicketEntryRow = {
  record: ManualEntrySource; partIndex: number; name: string; mode: "people" | "session";
  role: string; people: string; count: string; price: string; linked: boolean; editable: boolean;
};
const roleKeys = { 学生: "studentCount", 老师: "teacherCount", 导游: "guideCount", 司机: "driverCount" } as const;
const valueOf = (value = "") => /^(SKU名称|费用名称|角色|人数|次数|场次|场次数|单价)$/.test(value) ? "" : value;
const roleCount = (role: string, context: ManualEntryContext) => role in roleKeys ? String(context.people[roleKeys[role as keyof typeof roleKeys]] ?? 0) : "";

/** 同记录后续角色沿用最近一次显式项目名。 */
function partsOf(text: string) {
  let name = "";
  return text.normalize("NFKC").split(/[,，]/u).map(part => {
    const header = part.trim().match(/^#(.+?)\s+([\s\S]*)$/u);
    if (header) name = header[1];
    return { name, body: header ? header[2] : part.trim() };
  });
}

/** 从原文读取表格，保留同 SKU 多角色及未完成公式；金额继续交给既有解析器。 */
export function ticketEntryRows(text: string, context: ManualEntryContext): TicketEntryRow[] {
  return [...text.matchAll(/[^\n;；]+/gu)].filter(match => match[0].trim()).flatMap(match => {
    const start = (match.index ?? 0) + match[0].search(/\S/u), raw = match[0].trim();
    const record = { text: raw, start, end: start + raw.length, line: text.slice(0, start).split("\n").length };
    return partsOf(raw).map((part, partIndex) => {
      const roleMatch = part.body.match(/^(学生|老师|导游|司机|角色)\s*/u);
      const role = valueOf(roleMatch?.[1]);
      const factors = part.body.slice(roleMatch?.[0].length ?? 0).replace(/\s/gu, "").split(/[xX×*]/u)
        .map(value => value.replace(/(?<=\d)(?:人|次|场|元)$/u, ""));
      const linked = factors.length !== 3;
      return { record, partIndex, name: valueOf(part.name), mode: roleMatch ? "people" : "session", role,
        people: roleMatch ? linked ? roleCount(role, context) : valueOf(factors[0]) : "",
        count: valueOf(factors.at(-2)), price: valueOf(factors.at(-1)), linked,
        editable: Boolean(part.name && (factors.length === 2 || (roleMatch && factors.length === 3))),
      };
    });
  });
}

/** 修改一个角色片段时展开同记录其他片段，保持其他费用不变。 */
export function changeTicketEntryRow(text: string, row: TicketEntryRow, field: TicketEntryField, value: string, context: ManualEntryContext): string {
  if (!row.editable || /[\n\r;；,，#＃×*]/u.test(value)) return text;
  if (["people", "count", "price"].includes(field) && /[xX]/u.test(value)) return text;
  if (field === "mode" && value !== "people" && value !== "session") return text;
  if (field === "role" && !(TICKET_ENTRY_ROLES as readonly string[]).includes(value)) return text;
  const next = { ...row, [field]: value };
  if (field === "mode") { next.role = value === "people" ? "学生" : ""; next.linked = true; }
  if (field === "role") next.linked = true;
  if (field === "role" || field === "mode") next.people = roleCount(next.role, context);
  if (field === "people") next.linked = false;
  const formula = [next.mode === "people" && !next.linked ? next.people || "人数" : null, next.count || "次数", next.price || "单价"]
    .filter(part => part !== null).join("×");
  const record = `#${next.name.trim() || "费用名称"} ${next.mode === "people" ? `${next.role || "角色"} ` : ""}${formula}`;
  return replaceRecord(text, row, partsOf(row.record.text).map((part, index) => index === row.partIndex ? record : `#${part.name} ${part.body}`));
}

/** 删除当前角色费用，保留同 SKU 的其他角色。 */
export function removeTicketEntryRow(text: string, row: TicketEntryRow): string {
  return replaceRecord(text, row, partsOf(row.record.text).filter((_, index) => index !== row.partIndex).map(part => part.name ? `#${part.name} ${part.body}` : part.body));
}

/** 仅替换当前记录范围；删空时顺带移除相邻分隔符。 */
function replaceRecord(text: string, row: TicketEntryRow, records: string[]): string {
  if (records.length) return text.slice(0, row.record.start) + records.join("\n") + text.slice(row.record.end);
  let { start, end } = row.record;
  const after = text.slice(end).match(/^[\s;；]+/u)?.[0];
  if (after) end += after.length;
  else start -= text.slice(0, start).match(/[\s;；]*$/u)?.[0].length ?? 0;
  const result = text.slice(0, start) + text.slice(end);
  return /^[\s;；]*$/u.test(result) ? "" : result;
}

/** 新增行名称留空，由用户手填；其余默认按学生人数、一次、待补价录入。 */
export function appendTicketEntryRow(text: string): string {
  return `${text}${text && !text.endsWith("\n") ? "\n" : ""}#费用名称 学生 1×单价`;
}
